/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */

/**
 * Skill level controls UI complexity throughout the application.
 * - beginner: Simplified UI with minimal colors, limited fret range, hidden advanced features
 * - intermediate: Full interval colors, diatonic chords visible, most features available
 * - advanced: All features visible, full customization options
 */
export type SkillLevel = 'beginner' | 'intermediate' | 'advanced';

export const SKILL_LEVEL_STORAGE_KEY = 'guituitive-skill-level';

export const SKILL_LEVEL_LABELS: Record<SkillLevel, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

export const SKILL_LEVEL_DESCRIPTIONS: Record<SkillLevel, string> = {
  beginner: 'Simplified view - just the essentials',
  intermediate: 'Full theory visualization',
  advanced: 'All features and customization',
};

/**
 * Default fret range for each skill level
 */
export const SKILL_LEVEL_FRET_DEFAULTS: Record<SkillLevel, { min: number; max: number }> = {
  beginner: { min: 0, max: 5 },
  intermediate: { min: 0, max: 12 },
  advanced: { min: 0, max: 24 },
};

/**
 * Feature visibility by skill level
 */
export interface SkillLevelFeatures {
  showDiatonicChords: boolean;
  showPianoKeyboard: boolean;
  showInstrumentConfig: boolean;
  showFretRangeSelector: boolean;
  showAccidentalToggle: boolean;
  showDualViewMode: boolean;
  useSimplifiedColors: boolean;
  useSimplifiedLegend: boolean;
}

export const SKILL_LEVEL_FEATURES: Record<SkillLevel, SkillLevelFeatures> = {
  beginner: {
    showDiatonicChords: false,
    showPianoKeyboard: false,
    showInstrumentConfig: false,
    showFretRangeSelector: false,
    showAccidentalToggle: false,
    showDualViewMode: false,
    useSimplifiedColors: true,
    useSimplifiedLegend: true,
  },
  intermediate: {
    showDiatonicChords: true,
    showPianoKeyboard: false,
    showInstrumentConfig: true,
    showFretRangeSelector: true,
    showAccidentalToggle: true,
    showDualViewMode: false,
    useSimplifiedColors: false,
    useSimplifiedLegend: false,
  },
  advanced: {
    showDiatonicChords: true,
    showPianoKeyboard: true,
    showInstrumentConfig: true,
    showFretRangeSelector: true,
    showAccidentalToggle: true,
    showDualViewMode: true,
    useSimplifiedColors: false,
    useSimplifiedLegend: false,
  },
};

/**
 * Simplified interval labels for beginners.
 * Maps interval abbreviations to plain English names.
 */
export const BEGINNER_INTERVAL_LABELS: Record<string, string> = {
  'I': 'Root',
  'i': 'Root',
  'm2': '2nd',
  'M2': '2nd',
  'm3': '3rd',
  'M3': '3rd',
  'P4': '4th',
  'TRI': 'Tritone',
  'P5': '5th',
  'm6': '6th',
  'M6': '6th',
  'm7': '7th',
  'M7': '7th',
};

/**
 * Get interval label based on skill level.
 * - Beginner: Plain English (Root, 2nd, 3rd, etc.)
 * - Intermediate/Advanced: Abbreviations (R, M2, M3, etc.)
 */
export const getIntervalLabel = (
  abbreviation: string,
  useSimplifiedLabels: boolean
): string => {
  if (!useSimplifiedLabels) {
    return abbreviation;
  }
  return BEGINNER_INTERVAL_LABELS[abbreviation] || abbreviation;
};
